import { useLingui } from "@lingui/react/macro";
import { CalendarDays, ChevronDown } from "lucide-react";
import { lazy, Suspense, useState } from "react";

import { cn } from "@/shared/lib";
import { Button } from "@/shared/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { Skeleton } from "@/shared/ui/skeleton";

import { formatDayRange } from "../../lib/formatDayRange";
import type { MatchFilters } from "../../lib/matchFiltersSchema";
import { periodRange } from "../../lib/periodRange";
import { PERIOD_PRESETS } from "../../lib/PERIOD_PRESETS";
import { toIsoDay } from "../../lib/toIsoDay";
import { filterTriggerClassName } from "./filterTriggerClassName";
import { PERIOD_LABELS } from "./PERIOD_LABELS";

// Календарь с date-fns весит заметно больше остальной главной: грузится, когда открыли выбор периода.
const CustomPeriod = lazy(() =>
  import("./CustomPeriod").then((module) => ({ default: module.CustomPeriod })),
);

type Period = Pick<MatchFilters, "period" | "from" | "to">;

type PeriodFilterProps = {
  value: Period;
  // Дни, в которые были загруженные матчи: точки в календаре.
  matchDays: readonly Date[];
  now: Date;
  onChange: (period: Period) => void;
};

// Выбор периода: готовые периоды слева, свой — в календаре справа.
// Готовый период применяется сразу, свой — кнопкой «Применить».
export const PeriodFilter = ({ value, matchDays, now, onChange }: PeriodFilterProps) => {
  const { i18n, t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const range = periodRange(value, now);
  const isCustom = value.from !== undefined && range !== null;
  const label = isCustom
    ? formatDayRange(range, i18n.locale)
    : value.period === undefined
      ? t`All time`
      : i18n._(PERIOD_LABELS[value.period]);

  const pick = (period: Period) => {
    onChange(period);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={filterTriggerClassName(range !== null)}>
          <CalendarDays aria-hidden="true" className="text-subtle" />
          <span>{label}</span>
          <ChevronDown aria-hidden="true" className="text-subtle" />
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label={t`Choose a period`} className="flex w-[640px] flex-wrap gap-4">
        <div
          role="group"
          aria-label={t`Preset periods`}
          className="flex flex-[1_1_160px] flex-col gap-0.5"
        >
          {[undefined, ...PERIOD_PRESETS].map((preset) => {
            const isActive = !isCustom && value.period === preset;
            return (
              <Button
                key={preset ?? "all"}
                variant="ghost"
                aria-pressed={isActive}
                className={cn(
                  "h-9 justify-start rounded-lg px-3 text-sm font-normal",
                  isActive ? "bg-surface-raised font-semibold text-accent" : "text-foreground-soft",
                )}
                onClick={() => {
                  pick({ period: preset, from: undefined, to: undefined });
                }}
              >
                {preset === undefined ? t`All time` : i18n._(PERIOD_LABELS[preset])}
              </Button>
            );
          })}
        </div>
        <Suspense fallback={<Skeleton className="h-[380px] min-w-0 flex-[999_1_300px]" />}>
          <CustomPeriod
            initial={isCustom ? range : undefined}
            matchDays={matchDays}
            now={now}
            onApply={(custom) => {
              pick({ period: undefined, from: toIsoDay(custom.from), to: toIsoDay(custom.to) });
            }}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
};
